/**
 * Instanced GPU particles: one draw call per batch (additive or alpha). The ParticlePool's
 * typed arrays are the instance attributes; per frame only position/age and velocity upload,
 * static attributes upload only when something spawned. Size, colour and alpha over lifetime,
 * rotation and velocity stretch are evaluated in the vertex shader.
 */
import * as THREE from 'three';
import { ATLAS_COLS } from './textures';
import type { ParticlePool } from './pool';

const VERT = `
attribute vec4 iPosAge; attribute vec4 iVel; attribute vec4 iSize; attribute vec4 iColA; attribute vec4 iColB; attribute vec4 iMisc;
uniform float uFx; uniform float uCols;
varying vec2 vUv; varying vec4 vCol;
void main(){
  float t = iPosAge.w;
  if (t >= 1.0) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); vCol = vec4(0.0); vUv = vec2(0.0); return; }
  float life = iMisc.y;
  float flags = iMisc.z;
  // size: fast attack then ease toward size1
  float sizeT = 1.0 - (1.0 - t) * (1.0 - t);
  float size = mix(iSize.x, iSize.y, sizeT);
  vec4 mv = modelViewMatrix * vec4(iPosAge.xyz, 1.0);
  vec2 c = position.xy;
  if (iVel.w > 0.0) {
    vec3 vv = (modelViewMatrix * vec4(iVel.xyz, 0.0)).xyz;
    float l = length(vv.xy);
    vec2 d = l > 1e-4 ? vv.xy / l : vec2(0.0, 1.0);
    vec2 n = vec2(-d.y, d.x);
    float stretch = 1.0 + l * iVel.w;
    mv.xy += d * c.y * size * stretch + n * c.x * size;
  } else {
    float rot = iSize.z + iSize.w * t * life;
    float cs = cos(rot), sn = sin(rot);
    mv.xy += vec2(c.x * cs - c.y * sn, c.x * sn + c.y * cs) * size;
  }
  gl_Position = projectionMatrix * mv;
  float a = mix(iColA.a, iColB.a, t) * smoothstep(0.0, 0.06, t);
  float y = iPosAge.y - iMisc.w; // height above the floor under the spawn point
  if (mod(flags, 2.0) >= 1.0) a *= smoothstep(0.0, 0.35, y);
  if (mod(floor(flags / 4.0), 2.0) >= 1.0) a *= smoothstep(0.15, 0.9, y);
  vCol = vec4(mix(iColA.rgb, iColB.rgb, t), a * uFx);
  float f = iMisc.x;
  float col = mod(f, uCols);
  float row = floor(f / uCols);
  vUv = vec2((col + c.x + 0.5) / uCols, (uCols - 1.0 - row + c.y + 0.5) / uCols);
}`;

const FRAG = `
uniform sampler2D uMap;
varying vec2 vUv; varying vec4 vCol;
void main(){
  vec4 tex = texture2D(uMap, vUv);
  float a = tex.a * vCol.a;
  if (a < 0.004) discard;
  gl_FragColor = vec4(vCol.rgb * tex.rgb, a);
}`;

// Flags are decoded in the shader with mod/floor: bit 1 = PF_FLOOR_FADE, bit 4 = PF_GROUND_CLEAR (pool.ts).

export class ParticleBatch {
  readonly mesh: THREE.Mesh;
  private readonly geo: THREE.InstancedBufferGeometry;
  private readonly mat: THREE.ShaderMaterial;
  private readonly aPos: THREE.InstancedBufferAttribute;
  private readonly aVel: THREE.InstancedBufferAttribute;
  private readonly statics: THREE.InstancedBufferAttribute[];

  constructor(readonly pool: ParticlePool, atlas: THREE.Texture, fxUniform: { value: number }, additive: boolean, renderOrder: number) {
    const quad = new THREE.PlaneGeometry(1, 1);
    this.geo = new THREE.InstancedBufferGeometry();
    this.geo.index = quad.index;
    this.geo.setAttribute('position', quad.getAttribute('position'));
    const mk = (arr: Float32Array, dynamic: boolean): THREE.InstancedBufferAttribute => {
      const a = new THREE.InstancedBufferAttribute(arr, 4);
      a.setUsage(dynamic ? THREE.DynamicDrawUsage : THREE.StaticDrawUsage);
      return a;
    };
    this.aPos = mk(pool.posAge, true);
    this.aVel = mk(pool.vel, true);
    const aSize = mk(pool.sizeRot, true);
    const aColA = mk(pool.colA, true);
    const aColB = mk(pool.colB, true);
    const aMisc = mk(pool.misc, true);
    this.statics = [aSize, aColA, aColB, aMisc];
    this.geo.setAttribute('iPosAge', this.aPos);
    this.geo.setAttribute('iVel', this.aVel);
    this.geo.setAttribute('iSize', aSize);
    this.geo.setAttribute('iColA', aColA);
    this.geo.setAttribute('iColB', aColB);
    this.geo.setAttribute('iMisc', aMisc);
    this.geo.instanceCount = 0;
    this.mat = new THREE.ShaderMaterial({
      uniforms: { uMap: { value: atlas }, uFx: fxUniform, uCols: { value: ATLAS_COLS } },
      vertexShader: VERT,
      fragmentShader: FRAG,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      blending: additive ? THREE.AdditiveBlending : THREE.NormalBlending,
    });
    this.mesh = new THREE.Mesh(this.geo, this.mat);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = renderOrder;
  }

  /** Upload this frame's data. */
  sync(): void {
    const n = this.pool.hwm;
    this.geo.instanceCount = n;
    if (n === 0) return;
    const count = n * 4;
    this.aPos.clearUpdateRanges();
    this.aPos.addUpdateRange(0, count);
    this.aPos.needsUpdate = true;
    this.aVel.clearUpdateRanges();
    this.aVel.addUpdateRange(0, count);
    this.aVel.needsUpdate = true;
    if (this.pool.consumeDirty()) {
      for (const a of this.statics) {
        a.clearUpdateRanges();
        a.addUpdateRange(0, count);
        a.needsUpdate = true;
      }
    }
  }

  dispose(): void {
    this.mesh.removeFromParent();
    this.geo.dispose();
    this.mat.dispose();
  }
}
